import React, { useState, useEffect, useCallback } from 'react';
import CancelConfirmModal from '../components/CancelConfirmModal';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { API_BASE } from '../config';
import { ListHero, CountCard, ListTopRow, todayISO } from '../components/ListHero';
import { toDate as excelDate, num, exportSheet, todayStamp } from '../excel';
import './../Accounts.css';

// Any ISO-ish date string -> "07-Jul-26"
function formatInvoiceDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const day    = String(d.getUTCDate()).padStart(2, '0');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const mon    = months[d.getUTCMonth()];
    const yr     = String(d.getUTCFullYear()).slice(-2);
    return `${day}-${mon}-${yr}`;
}

function formatCurrency(v) {
    return v === null || v === undefined ? '—' : `₹${v}`;
}

// Lists real generated invoices (invoice_headers, populated by Power
// Automate PDF processing). Clicking a row opens a modal (same interaction
// as Invoice Queue/SO Queue) showing every invoice_headers/invoice_line_items
// field (excluding log/audit tables). Cancelling is a single top-level
// action — search for the invoice by no./PO/buyer, pick it from the
// results, then the same two-step (reason + checkbox) confirm.
export default function Invoices({ user }) {
    const [invoices, setInvoices] = useState([]);
    const [search, setSearch]     = useState('');
    // Defaults to today, same as Gifting & Dispatch.
    const [fromDate, setFromDate] = useState(todayISO());
    const [toDate, setToDate]     = useState(todayISO());
    const [total, setTotal]       = useState(0);
    const [exporting, setExporting] = useState(false);
    const [sortDir, setSortDir]   = useState('desc');
    const [page, setPage]         = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);

    const [detailTarget, setDetailTarget] = useState(null); // invoice_no or null
    const [detailCache, setDetailCache] = useState({}); // invoice_no -> { header, line_items }
    const [detailLoading, setDetailLoading] = useState(false);

    const [imagesOpen, setImagesOpen]       = useState(false);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [images, setImages]               = useState(null);
    const [pdfLoading, setPdfLoading]       = useState(false);
    const [packingSlipLoading, setPackingSlipLoading] = useState(false);

    const [imageExists, setImageExists] = useState({}); // order_ref_no -> bool

    const [cancelSearchOpen, setCancelSearchOpen] = useState(false);
    const [cancelQuery, setCancelQuery]           = useState('');
    const [cancelTarget, setCancelTarget]         = useState(null);
    const [confirming, setConfirming]             = useState(false);
    const [message, setMessage]   = useState(null);
    const [discrepancies, setDiscrepancies] = useState(null); // { mismatchCount, missingCount }
    const displayName = localStorage.getItem('display_name') || 'Accounts';

    useEffect(() => {
        (async () => {
            try {
                const [mmRes, miRes] = await Promise.all([
                    fetch(`${API_BASE}/acc-so-invoice-check?action=qty_mismatch`),
                    fetch(`${API_BASE}/acc-so-invoice-check?action=missing_invoices`),
                ]);
                const mmData = await mmRes.json();
                const miData = await miRes.json();
                setDiscrepancies({
                    mismatchCount: mmData.success ? (mmData.mismatches || []).length : 0,
                    missingCount:  miData.success ? (miData.missing || []).length : 0,
                });
            } catch {
                // Non-critical — silently skip the banner if diagnostics are unreachable.
            }
        })();
    }, []);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ action: 'list_invoices', search, sort_dir: sortDir, page: String(page) });
            if (fromDate) params.set('from_date', fromDate);
            if (toDate)   params.set('to_date', toDate);
            const res = await fetch(`${API_BASE}/acc-invoices?${params.toString()}`);
            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            const data = await res.json();
            setInvoices(data.invoices || []);
            setTotalPages(data.total_pages || 1);
            setTotal(data.total || 0);
        } catch (err) {
            console.error('Failed to load invoices', err);
            setError(err.message);
            setInvoices([]);
            setTotalPages(1);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [search, fromDate, toDate, sortDir, page]);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    // Deep-link from a notification click (?q=<invoice_no>).
    useEffect(() => {
        const q = new URLSearchParams(window.location.search).get('q');
        // Drop the default today-only range, or an older invoice would never match.
        if (q) { setSearch(q); setFromDate(''); setToDate(''); }
    }, []);

    // Any filter/sort change invalidates the current page — jump back to 1
    // rather than risk landing past the new result set's last page.
    useEffect(() => { setPage(1); }, [search, fromDate, toDate, sortDir]);

    // Box-image presence dot — one bulk fuzzy-match request per page load
    // (not per row/click), unlike the on-demand per-invoice "Show Images"
    // fetch below.
    useEffect(() => {
        (async () => {
            const orderRefNos = [...new Set(invoices.map(inv => inv.order_ref_no).filter(Boolean))];
            if (orderRefNos.length === 0) { setImageExists({}); return; }
            try {
                const res  = await fetch(`${API_BASE}/acc-invoices?action=images_exist&order_ref_nos=${encodeURIComponent(orderRefNos.join(','))}`);
                const data = await res.json();
                setImageExists(res.ok && data.success ? data.results : {});
            } catch (err) {
                console.error('Failed to check image presence', err);
                setImageExists({});
            }
        })();
    }, [invoices]);

    // The record-visits 'mark' call was removed along with this section's
    // sidebar badge — with nothing consuming the visit data it was just a POST
    // per row-open. record-visits.js still supports 'mark' for the sections
    // that do still show a badge (Invoice Queue, Distributor Proforma Invoice).

    const openDetail = async (invoiceNo) => {
        setDetailTarget(invoiceNo);
        if (detailCache[invoiceNo]) return; // already fetched once

        setDetailLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/acc-invoices?action=invoice_detail&invoice_no=${encodeURIComponent(invoiceNo)}`);
            const data = await res.json();
            if (res.ok && data.success) {
                setDetailCache(prev => ({ ...prev, [invoiceNo]: { header: data.header, line_items: data.line_items } }));
            }
        } catch (err) {
            console.error('Failed to load invoice detail', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setDetailTarget(null);
        setImagesOpen(false);
        setImages(null);
    };

    const showImages = async (orderRefNo) => {
        setImagesOpen(true);
        setImagesLoading(true);
        setImages(null);
        try {
            const res  = await fetch(`${API_BASE}/acc-invoices?action=invoice_images&order_ref_no=${encodeURIComponent(orderRefNo || '')}`);
            const data = await res.json();
            setImages(res.ok && data.success ? data.images : []);
        } catch (err) {
            console.error('Failed to load PO images', err);
            setImages([]);
        } finally {
            setImagesLoading(false);
        }
    };

    const showInvoicePdf = async (invoiceNo) => {
        setPdfLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/acc-invoices?action=invoice_pdf&invoice_no=${encodeURIComponent(invoiceNo)}`);
            const data = await res.json();
            if (res.ok && data.success && data.pdf_url) {
                window.open(data.pdf_url, '_blank', 'noopener');
            } else {
                setMessage({ type: 'error', text: 'No PDF available for this invoice.' });
            }
        } catch (err) {
            console.error('Failed to load invoice PDF', err);
            setMessage({ type: 'error', text: 'Failed to load invoice PDF.' });
        } finally {
            setPdfLoading(false);
        }
    };

    const downloadPackingSlip = async (orderRefNo) => {
        setPackingSlipLoading(true);
        try {
            const res = await fetch(`${API_BASE}/so-packing-slip-download-zip?action=packing_slip_for_order_ref&order_ref_no=${encodeURIComponent(orderRefNo || '')}`);
            const contentType = res.headers.get('content-type') || '';
            if (!res.ok || contentType.includes('json')) {
                setMessage({ type: 'error', text: 'Packing Slip is not available for this invoice' });
                return;
            }
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url;
            a.download = `Packing_Slip_${detailTarget}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download packing slip', err);
            setMessage({ type: 'error', text: 'Failed to download packing slip.' });
        } finally {
            setPackingSlipLoading(false);
        }
    };

    // Exports every invoice matching the current search/date range, not just
    // the 50 on screen — list_invoices is fixed at 50 per page, so walk them.
    const downloadExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const all = [];
            let pages = 1;
            for (let p = 1; p <= pages; p++) {
                const params = new URLSearchParams({ action: 'list_invoices', search, sort_dir: sortDir, page: String(p) });
                if (fromDate) params.set('from_date', fromDate);
                if (toDate)   params.set('to_date', toDate);
                const res = await fetch(`${API_BASE}/acc-invoices?${params.toString()}`);
                if (!res.ok) throw new Error(`Server returned ${res.status}`);
                const data = await res.json();
                pages = data.total_pages || 1;
                all.push(...(data.invoices || []));
            }
            if (all.length === 0) { setMessage({ type: 'error', text: 'Nothing to export for these filters.' }); return; }
            exportSheet({
                headers: ['Invoice Date', 'Invoice No', 'Buyer', 'Consignee', 'PO Number', 'Qty', 'Taxable Amount', 'Tax Amount', 'Net Amount', 'PDF Available'],
                rows: all.map(inv => [
                    excelDate(inv.invoice_date),
                    inv.invoice_no,
                    inv.buyer || '',
                    inv.consignee || '',
                    inv.order_ref_no || '',
                    num(inv.total_quantity),
                    num(inv.taxable_amount),
                    num(inv.tax_amount),
                    num(inv.net_amount),
                    inv.blob_url ? 'Yes' : 'No',
                ]),
                sheetName: 'Invoices',
                filename: `Invoices_${todayStamp()}.xlsx`,
            });
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Failed to build the Excel file' });
        } finally {
            setExporting(false);
        }
    };

    const cancelSearchResults = cancelQuery.trim()
        ? invoices.filter(inv => {
            const q = cancelQuery.trim().toLowerCase();
            return (inv.invoice_no || '').toLowerCase().includes(q)
                || (inv.order_ref_no || '').toLowerCase().includes(q)
                || (inv.buyer || '').toLowerCase().includes(q)
                || (inv.consignee || '').toLowerCase().includes(q);
        })
        : invoices;

    const pickForCancel = (inv) => {
        setCancelSearchOpen(false);
        setCancelQuery('');
        setCancelTarget(inv);
    };

    const handleConfirmCancel = async (reason) => {
        if (!cancelTarget) return;
        setConfirming(true);
        setMessage(null);
        try {
            const res = await fetch(`${API_BASE}/acc-cancel?action=cancel_invoice`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    invoice_no:   cancelTarget.invoice_no,
                    reason,
                    cancelled_by: displayName
                })
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || 'Failed to cancel invoice');

            setMessage({ type: 'success', text: `Invoice ${cancelTarget.invoice_no} cancelled.` });
            setCancelTarget(null);
            closeDetail();
            fetchInvoices();
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setConfirming(false);
        }
    };

    return (
        <>
            <ListHero
                kicker="Invoicing"
                title="Invoices"
                hint="Click on any invoice to see images, invoice PDF or packing slip."
                icons={['🧾', '📄', '📦', '💳', '🧾', '📑']}
                onDownload={downloadExcel}
                downloading={exporting}
                downloadTitle="Download every invoice matching the current filters"
            />

            <ListTopRow card={<CountCard from={fromDate} to={toDate} count={total} label="Invoices made" loading={loading} />}>
                <div style={{ height: '100%', boxSizing: 'border-box', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center', background: '#fff', border: '1px solid #ECE7F2', borderRadius: 18, boxShadow: '0 4px 14px rgba(35,28,48,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }} data-tutorial="invoices-search">
                        <input
                            className="acc-page__search"
                            style={{ flex: '1 1 220px' }}
                            placeholder="Search invoice no, buyer, PO no..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button className="acc-btn acc-btn--danger" onClick={() => {
                            if (!hasEditAccess(user, 'invoicing', 'invoices')) return notifyEditBlocked();
                            setCancelSearchOpen(true);
                        }}>
                            Cancel Invoice
                        </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13, color: 'var(--acc-text-muted)' }}>
                        <button
                            className="acc-btn"
                            onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                            title={sortDir === 'asc' ? 'Sorting oldest first — click for newest first' : 'Sorting newest first — click for oldest first'}
                        >
                            Date {sortDir === 'asc' ? '▲' : '▼'}
                        </button>
                        <span style={{ fontWeight: 600 }}>From</span>
                        <input type="date" className="acc-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        <span style={{ fontWeight: 600 }}>to</span>
                        <input type="date" className="acc-input" value={toDate} onChange={e => setToDate(e.target.value)} />
                        {(fromDate !== todayISO() || toDate !== todayISO()) && (
                            <button className="acc-btn" onClick={() => { setFromDate(todayISO()); setToDate(todayISO()); }}>Today</button>
                        )}
                        {(fromDate || toDate) && (
                            <button className="acc-btn" onClick={() => { setFromDate(''); setToDate(''); }}>All time</button>
                        )}
                    </div>
                    {discrepancies && (discrepancies.mismatchCount > 0 || discrepancies.missingCount > 0) && (
                        <div style={{ fontSize: 12.5, color: 'var(--acc-danger)' }}>
                            ⚠ {discrepancies.mismatchCount > 0 && `${discrepancies.mismatchCount} SO${discrepancies.mismatchCount !== 1 ? 's' : ''} with qty mismatches`}
                            {discrepancies.mismatchCount > 0 && discrepancies.missingCount > 0 && ' · '}
                            {discrepancies.missingCount > 0 && `${discrepancies.missingCount} SO${discrepancies.missingCount !== 1 ? 's' : ''} missing invoices`}
                        </div>
                    )}
                </div>
            </ListTopRow>

            {message && (
                <p className="acc-page__empty" style={{ color: message.type === 'error' ? 'var(--acc-danger)' : 'var(--acc-success)' }}>
                    {message.text}
                </p>
            )}

            {loading ? (
                <p className="acc-page__empty">Loading...</p>
            ) : error ? (
                <p className="acc-page__empty" style={{ color: 'var(--acc-danger)' }}>Failed to load: {error}</p>
            ) : invoices.length === 0 ? (
                <p className="acc-page__empty">No invoices found.</p>
            ) : (
                <div className="acc-grid">
                    <div className="acc-grid-header" style={{ gridTemplateColumns: '0.4fr 0.8fr 1fr 1.4fr 1fr 0.6fr 1fr 0.7fr 0.7fr' }}>
                        <div>Sr.</div>
                        <div>Date</div>
                        <div>Invoice No.</div>
                        <div>Buyer</div>
                        <div>PO</div>
                        <div>Qty</div>
                        <div>Net Amount</div>
                        <div>PDF</div>
                        <div>Image</div>
                    </div>
                    {invoices.map((inv, idx) => (
                        <div
                            key={inv.invoice_no}
                            className="acc-grid-row acc-grid-row--clickable"
                            style={{ gridTemplateColumns: '0.4fr 0.8fr 1fr 1.4fr 1fr 0.6fr 1fr 0.7fr 0.7fr' }}
                            onClick={() => openDetail(inv.invoice_no)}
                        >
                            <div className="acc-grid-cell acc-grid-cell--muted">{idx + 1}</div>
                            <div className="acc-grid-cell">{formatInvoiceDate(inv.invoice_date)}</div>
                            <div className="acc-grid-cell" style={{ fontWeight: 700 }}>{inv.invoice_no}</div>
                            <div className="acc-grid-cell">{inv.buyer || inv.consignee || '—'}</div>
                            <div className="acc-grid-cell acc-grid-cell--muted">{inv.order_ref_no || '—'}</div>
                            <div className="acc-grid-cell">{inv.total_quantity ?? '—'}</div>
                            <div className="acc-grid-cell">{formatCurrency(inv.net_amount)}</div>
                            <div className="acc-grid-cell">
                                <span className={`acc-dot acc-dot--${inv.blob_url ? 'success' : 'danger'}`} title={inv.blob_url ? 'PDF available' : 'No PDF'} />
                            </div>
                            <div className="acc-grid-cell">
                                <span className={`acc-dot acc-dot--${imageExists[inv.order_ref_no] ? 'success' : 'danger'}`} title={imageExists[inv.order_ref_no] ? 'Box image available' : 'No box image'} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && invoices.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 20 }}>
                    <button
                        className="acc-btn"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1}
                    >
                        ← Prev
                    </button>
                    <span style={{ fontSize: 13, color: 'var(--acc-text-muted)' }}>
                        Page {page} of {totalPages}
                    </span>
                    <button
                        className="acc-btn"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                    >
                        Next →
                    </button>
                </div>
            )}

            {detailTarget && (() => {
                const detail = detailCache[detailTarget];
                return (
                    <div className="acc-modal-overlay" onClick={closeDetail}>
                        <div className="acc-modal" style={{ maxWidth: '95vw', width: 1200 }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <h3>Invoice {detailTarget}</h3>
                                <button className="acc-btn" onClick={closeDetail}>Close</button>
                            </div>

                            {!detail ? (
                                <p className="acc-page__empty">{detailLoading ? 'Loading...' : 'No details found.'}</p>
                            ) : (
                                <>
                                    <div style={{
                                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '18px 24px',
                                        padding: '18px 26px', margin: '16px -26px', borderTop: '1px solid var(--acc-border)', borderBottom: '1px solid var(--acc-border)',
                                        background: 'var(--acc-card-detail-bg)'
                                    }}>
                                        {[
                                            { label: 'Invoice No.', value: detail.header.invoice_no ?? '—' },
                                            { label: 'Invoice Date', value: detail.header.invoice_date ? String(detail.header.invoice_date).slice(0, 10) : '—' },
                                            { label: 'PO / Order Ref No.', value: detail.header.order_ref_no ?? '—' },
                                            { label: 'Buyer', value: detail.header.buyer ?? '—' },
                                            { label: 'Consignee', value: detail.header.consignee ?? '—' },
                                            { label: 'Total Quantity', value: detail.header.total_quantity ?? '—' },
                                            { label: 'Taxable Amount', value: formatCurrency(detail.header.taxable_amount) },
                                            { label: 'Tax Amount', value: formatCurrency(detail.header.tax_amount) },
                                            { label: 'Net Amount', value: formatCurrency(detail.header.net_amount), accent: true },
                                        ].map((item, i) => (
                                            <div key={i}>
                                                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--acc-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                                                    {item.label}
                                                </div>
                                                <div style={{ fontSize: 15, fontWeight: item.accent ? 700 : 600, color: item.accent ? 'var(--acc-primary)' : 'var(--acc-text)' }}>
                                                    {item.value}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button className="acc-btn acc-btn--lavender" onClick={() => showImages(detail.header.order_ref_no)}>
                                            Show Images
                                        </button>
                                        <button className="acc-btn acc-btn--lavender" disabled={pdfLoading} onClick={() => showInvoicePdf(detailTarget)}>
                                            {pdfLoading ? 'Loading...' : 'Show Invoice PDF'}
                                        </button>
                                        <button className="acc-btn acc-btn--lavender" disabled={packingSlipLoading} onClick={() => downloadPackingSlip(detail.header.order_ref_no)}>
                                            {packingSlipLoading ? 'Loading...' : 'Packing Slip'}
                                        </button>
                                    </div>

                                    <div style={{ overflowX: 'auto', marginTop: 16 }}>
                                        <table className="acc-table">
                                            <thead>
                                                <tr>
                                                    <th>Sl No</th>
                                                    <th>SKU</th>
                                                    <th>EAN</th>
                                                    <th>HSN</th>
                                                    <th>MRP</th>
                                                    <th>Qty</th>
                                                    <th>Rate</th>
                                                    <th>Discount</th>
                                                    <th>Taxable Amt</th>
                                                    <th>Tax Type</th>
                                                    <th>IGST Rate</th>
                                                    <th>IGST Amt</th>
                                                    <th>CGST Rate</th>
                                                    <th>CGST Amt</th>
                                                    <th>SGST Rate</th>
                                                    <th>SGST Amt</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.line_items.map(li => (
                                                    <tr key={li.id}>
                                                        <td>{li.sl_no}</td>
                                                        <td>{li.sku_name}</td>
                                                        <td>{li.ean}</td>
                                                        <td>{li.hsn}</td>
                                                        <td>{formatCurrency(li.mrp)}</td>
                                                        <td>{li.quantity}</td>
                                                        <td>{formatCurrency(li.rate)}</td>
                                                        <td>{li.discount}</td>
                                                        <td>{formatCurrency(li.taxable_amount)}</td>
                                                        <td>{li.tax_type ?? '—'}</td>
                                                        <td>{li.igst_rate ?? '—'}</td>
                                                        <td>{formatCurrency(li.igst_amount)}</td>
                                                        <td>{li.cgst_rate ?? '—'}</td>
                                                        <td>{formatCurrency(li.cgst_amount)}</td>
                                                        <td>{li.sgst_rate ?? '—'}</td>
                                                        <td>{formatCurrency(li.sgst_amount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}

            {imagesOpen && (
                <div className="acc-modal-overlay" onClick={() => setImagesOpen(false)}>
                    <div className="acc-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Box Images</h3>
                            <button className="acc-btn" onClick={() => setImagesOpen(false)}>Close</button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16, minHeight: 60 }}>
                            {imagesLoading && <p className="acc-page__empty" style={{ padding: 0 }}>Loading…</p>}
                            {!imagesLoading && images && images.length === 0 && (
                                <p className="acc-page__empty" style={{ padding: 0 }}>No images found.</p>
                            )}
                            {!imagesLoading && images && images.map(img => (
                                <a key={img.id} href={img.view_url || undefined} target="_blank" rel="noreferrer">
                                    <img
                                        src={img.view_url}
                                        alt={img.blob_name}
                                        style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--acc-border)' }}
                                    />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {cancelSearchOpen && (
                <div className="acc-modal-overlay" onClick={() => setCancelSearchOpen(false)}>
                    <div className="acc-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                        <h3>Cancel an Invoice</h3>
                        <p>Search by invoice no., PO number, or buyer name, then pick the invoice to cancel.</p>
                        <input
                            className="acc-page__search"
                            style={{ width: '100%', boxSizing: 'border-box', marginBottom: 14 }}
                            placeholder="Search invoice no, PO no, buyer..."
                            value={cancelQuery}
                            onChange={e => setCancelQuery(e.target.value)}
                            autoFocus
                        />
                        <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {cancelSearchResults.length === 0 ? (
                                <p className="acc-page__empty" style={{ padding: 0 }}>No matching invoices.</p>
                            ) : cancelSearchResults.map(inv => (
                                <div
                                    key={inv.invoice_no}
                                    className="acc-so-card"
                                    style={{ padding: '10px 14px', cursor: 'pointer' }}
                                    onClick={() => pickForCancel(inv)}
                                >
                                    <strong>{inv.invoice_no}</strong>
                                    <span className="acc-so-card__buyer">{inv.buyer || inv.consignee || '—'} · PO: {inv.order_ref_no || '—'}</span>
                                </div>
                            ))}
                        </div>
                        <div className="acc-modal-actions">
                            <button className="acc-btn" onClick={() => setCancelSearchOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {cancelTarget && (
                <CancelConfirmModal
                    title="Cancel this Invoice?"
                    itemLabel={`Invoice ${cancelTarget.invoice_no}`}
                    confirming={confirming}
                    onConfirm={handleConfirmCancel}
                    onClose={() => setCancelTarget(null)}
                />
            )}
        </>
    );
}
